import { IonList } from "@ionic/react";
import { snakeCase } from "lodash";
import type { HslColor } from "react-colorful";
import { useHistory } from "react-router-dom";
import { pillarIconComponents } from "shared/components/icons/PillarIcon";
import TemplateComponent from "shared/components/template/TemplateComponent";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { adjustForContrast } from "shared/helpers/theme/color-helpers";
import type { AppRouteConfigView } from "shared/mappers/database/app-route/app-route-config/app-route-config";
import HubSideMenuItem from "hub/src/components/sideMenu/HubSideMenuItem";
import HubSideMenuItemModal from "hub/src/components/sideMenu/HubSideMenuItemModal";
import useDeviceInfo from "hub/src/hooks/useDeviceInfo";
import type { HubRouteConfig } from "hub/src/routes/RouteConfigWrapper";
import { mapPathParamsToUrl } from "hub/src/utils/mapPathParamsToUrl";
import { isRouteVisible } from "hub/src/utils/routes/isRouteVisible";

export type MenuItemLinkProps = HubRouteConfig & {
  color?: HslColor;
  template?: string;
  onClick?: () => void;
};

export const MenuItemLink = (props: MenuItemLinkProps) => {
  const { color, template, onClick, ...route } = props;

  const device = useDeviceInfo();
  const history = useHistory();
  const session = useCustomSession();

  const routeVisible = isRouteVisible({ device, session, route });
  const childRouteVisible =
    !!route?.childrenAppRouteConfig &&
    !!route.childrenAppRouteConfig.length &&
    route.childrenAppRouteConfig.some((child) =>
      isRouteVisible({ device, session, route: child }),
    );

  // If the route is not a user hub nav type
  if (!routeVisible) {
    return null;
  }

  const { lightText, bg } = props?.color ? adjustForContrast(props.color) : {};
  const textColor = lightText ? "!text-neutral-light" : "!text-neutral-dark";

  const IconComponent = props.iconName
    ? pillarIconComponents[props.iconName]
    : () => <></>;

  return (
    <>
      <button
        data-testid={[
          "main-menu",
          props.parentAppRouteConfig
            ? snakeCase(props.parentAppRouteConfig.label)
            : "",
          snakeCase(props.label),
        ]
          .filter((part) => !!part)
          .join("-")}
        onClick={() => {
          if (props.externalRoute) {
            props.openInNewTab
              ? window.open(props.url)
              : (window.location.href = props.url!);
          } else {
            history.push(mapPathParamsToUrl(props.url!, props.routeParams!));
          }

          props.onClick?.();
        }}
        className={`sidebar-menu-selector ${!!template && "overflow-hidden !p-0"} ${!!bg && textColor} ${props.parentAppRouteConfig ? "!pl-3.5" : ""}`}
        style={{
          backgroundColor: bg,
        }}
      >
        {template ? (
          <TemplateComponent
            className="size-full"
            template={template}
            data={route}
            title={childRouteVisible ? "Main" : props.label}
          />
        ) : (
          <>
            {IconComponent && (
              <IconComponent className="sidebar-menu-svg ion-like-icon" />
            )}
            <div className="sidebar-menu-title">
              {childRouteVisible ? "Main" : props.label}
            </div>
          </>
        )}
      </button>
    </>
  );
};

export enum ROUTES_LIST_VARIANT {
  LIST = "list",
  TILE = "tile",
}

type HubRoutesListProps = {
  className?: string;
  variant?: ROUTES_LIST_VARIANT;
  routes: AppRouteConfigView[];
  onRouteClick?: (route: AppRouteConfigView) => void;
};

export const HubRoutesList = ({
  className,
  variant = ROUTES_LIST_VARIANT.LIST,
  routes,
  onRouteClick,
}: HubRoutesListProps) => {
  const device = useDeviceInfo();
  const session = useCustomSession();

  const isTile = variant === ROUTES_LIST_VARIANT.TILE;

  const renderMenuItems = (routeConfigs: AppRouteConfigView[]) => {
    return routeConfigs?.map((route) => {
      const routeVisible = isRouteVisible({ device, session, route });
      const childRouteVisible =
        !!route?.childrenAppRouteConfig &&
        !!route.childrenAppRouteConfig.length &&
        route.childrenAppRouteConfig.some((child) =>
          isRouteVisible({ device, session, route: child }),
        );
      const shouldRender = routeVisible || childRouteVisible;

      // Skip routes with no visible children (submenu) or invisible standalone routes
      if (!shouldRender) {
        return null; // Skip rendering this route
      }

      if (
        route.parentAppRouteConfig &&
        routeConfigs.some((r) => r.id === route.parentAppRouteConfig?.id)
      ) {
        // If the route has a parent that is supposed to be on the user hub nav, then the child should not be rendered
        return null;
      }

      const IconComponent = route.iconName
        ? pillarIconComponents[route.iconName]
        : undefined;

      if (childRouteVisible) {
        const SideMenuItemComponent = isTile
          ? HubSideMenuItemModal
          : HubSideMenuItem;

        return (
          <SideMenuItemComponent
            key={`route-nav-${route.id || route.label}`}
            label={route.label}
            initialIsOpen={false}
            buttonClassName="sidebar-menu-selector"
            chevronIconClassname="sidebar-menu-svg ion-like-icon size-2 phone:size-3"
            panelClassName={isTile ? "sidebar-menu-tile-panel" : ""}
            IconComponent={IconComponent}
          >
            <MenuItemLink
              {...(route as HubRouteConfig)}
              onClick={() => onRouteClick?.(route)}
            />
            {renderMenuItems(route.childrenAppRouteConfig!)}
          </SideMenuItemComponent>
        );
      }

      return (
        <MenuItemLink
          key={`route-nav-${route.id || route.label}`}
          {...(route as HubRouteConfig)}
          onClick={() => onRouteClick?.(route)}
        />
      );
    });
  };

  return (
    <IonList
      inset={true}
      className={`${isTile ? "sidebar-menu-tile" : "flex flex-col px-2 py-1 !m-0"} ${className ?? ""}`}
    >
      {renderMenuItems(routes)}
    </IonList>
  );
};
